"use client";

import { useEffect } from "react";
import { useTranslations } from "next-intl";

import { Link } from "@/i18n/navigation";

type Props = {
  error: Error & { digest?: string };
  reset: () => void;
};

export default function LocaleError({ error, reset }: Props) {
  const t = useTranslations("error");

  useEffect(() => {
    console.error("[locale error boundary]", error);
  }, [error]);

  return (
    <main
      id="main"
      className="relative isolate flex min-h-[70vh] items-center justify-center overflow-hidden px-5 pt-[120px] pb-16 md:px-10 md:pt-[160px] md:pb-24"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(60% 55% at 20% 10%, rgba(30,63,232,0.18) 0%, transparent 60%), radial-gradient(40% 40% at 85% 95%, rgba(34,211,238,0.10) 0%, transparent 65%)",
        }}
      />
      <div className="relative mx-auto flex w-full max-w-[1100px] flex-col items-start gap-10">
        <p
          className="inline-flex items-center gap-[10px] font-mono text-[12px] uppercase text-brand-azure"
          style={{ letterSpacing: "0.18em" }}
        >
          <span
            aria-hidden
            className="inline-block h-px w-6"
            style={{
              background: "var(--brand-azure)",
              boxShadow: "0 0 8px var(--brand-azure)",
            }}
          />
          {t("eyebrow")}
        </p>

        <h1
          className="m-0 max-w-[1000px] font-display font-bold text-fg1"
          style={{
            fontSize: "clamp(36px, 5.5vw, 80px)",
            lineHeight: 1.05,
            letterSpacing: "-0.03em",
          }}
        >
          {t("title")}
        </h1>

        <p className="max-w-[620px] text-[16px] leading-relaxed text-fg2 md:text-[18px]">
          {t("lede")}
        </p>

        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <button
            type="button"
            onClick={reset}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 font-display text-[15px] font-semibold text-white sm:w-auto sm:justify-start"
            style={{
              border: "none",
              background:
                "linear-gradient(135deg, var(--brand-electric) 0%, var(--brand-azure) 100%)",
              boxShadow:
                "0 0 0 1px rgba(59,126,255,0.45), 0 12px 32px rgba(30,63,232,0.35)",
            }}
          >
            <span>{t("tryAgain")}</span>
            <span aria-hidden>↻</span>
          </button>

          <Link
            href="/"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border px-6 py-3 font-display text-[15px] font-semibold text-fg1 transition-colors sm:w-auto sm:justify-start"
            style={{
              borderColor: "var(--border-strong)",
              background: "transparent",
            }}
          >
            <span>{t("goHome")}</span>
          </Link>
        </div>
      </div>
    </main>
  );
}
